import { AllocationRequirementDataRow } from '@warebee/shared/export-converter';
import { TFunction } from 'i18next';
import _ from 'lodash';
import { formatInteger } from '../common/formatHelper';
import useFormatter from '../common/useFormatter';
import { ColumnConfig } from '../components/DatasetTable';
import ItemCategoryTag from '../components/table/ItemCategoryTag';
import Tag from '../helpContext/Tag';
import EmptyValueTag from '../simulation/tags/EmptyValueTag';
import ItemTag from '../simulation/tags/ItemTag';

export function getAllocationRequirementTableConfig(
  uoms: string[],
  t: TFunction<'dataset'>,
  rows: AllocationRequirementDataRow[] = [],
): ColumnConfig<AllocationRequirementDataRow>[] {
  const formatter = useFormatter();

  function renderQtyValue(value: number) {
    if (value === undefined) {
      return <EmptyValueTag />;
    }
    return <span className="w-full text-right">{formatInteger(value)}</span>;
  }

  function renderRules(rulesIds: string[] | string) {
    if (typeof rulesIds === 'string') {
      rulesIds = [rulesIds];
    }
    return _.map(rulesIds, (ruleId, index) => {
      return <Tag key={`tag-${ruleId}-${index}`}>{ruleId}</Tag>;
    });
  }

  // Generate dynamic filter options for picking rules
  const pickingRuleSet = new Set<string>();
  (rows ?? []).forEach(row => {
    if (Array.isArray(row.orderLineRuleId)) {
      row.orderLineRuleId.forEach(rule => pickingRuleSet.add(rule));
    } else if (row.orderLineRuleId) {
      pickingRuleSet.add(row.orderLineRuleId);
    }
  });
  const pickingRuleOptions = {
    '': t('All', { ns: 'dataset' }),
    ...Object.fromEntries(Array.from(pickingRuleSet).map(rule => [rule, rule])),
  };

  // Generate dynamic filter options for other string columns
  const generateFilterOptions = (fieldName: string) => {
    const uniqueValues = new Set<string>();
    (rows ?? []).forEach(row => {
      const value = (row as any)[fieldName];
      if (value && typeof value === 'string') {
        uniqueValues.add(value);
      }
    });
    return {
      '': t('All', { ns: 'dataset' }),
      ...Object.fromEntries(
        Array.from(uniqueValues).map(value => [value, value]),
      ),
    };
  };

  const uomColumns = _.flatMap(uoms, uom => {
    const uomDescription = `UOM: {{uomUnit}}`;
    const translationOptions = {
      uomUnit: uom,
      ns: 'dataset',
    };

    return [
      {
        field: uom,
        title: t(uomDescription, translationOptions),
        render: renderQtyValue,
        classNameTd: '!bg-menu-500/[0.3]',
        hasSort: true,
        hasFilter: true,
      },
      {
        field: `${uom}-agg`,
        title: `${uom} (Req. avg.)`,
        render: renderQtyValue,
        classNameTd: '!bg-menu-500/[0.2]',
        hasSort: true,
        hasFilter: true,
      },
    ];
  });

  return [
    {
      field: 'orderLineRule',
      title: t(`Picking Rule`, { ns: 'dataset' }),
      hasSort: true,
      hasFilter: true,
      filterOptions: pickingRuleOptions,
      render: (values: string[]) => renderRules(values),
    },
    {
      field: 'consignee',
      title: t(`Consignee`, { ns: 'dataset' }),
      isHeader: true,
      hasSort: true,
      hasFilter: true,
      filterOptions: generateFilterOptions('consignee'),
    },
    {
      field: 'sku',
      title: t(`Item`, { ns: 'dataset' }),
      isHeader: true,
      hasSort: true,
      hasFilter: true,
      filterOptions: generateFilterOptions('sku'),
      render: (sku: string, row) => (
        <ItemTag title={sku} filters={{ consignee: row['consignee'], sku }} />
      ),
    },
    {
      field: 'cumulativePercentRank',
      title: t(`ABC`, { ns: 'dataset' }),
      hiddenInExport: true,
      hasSort: true,
      hasFilter: true,
      filterOptions: generateFilterOptions('cumulativePercentRank'),
      render: (v: number) => (
        <div className="flex items-center gap-1">
          <ItemCategoryTag cmlPercent={v} className="min-w-10" />
          <span className="text-xs opacity-70">
            {formatter.formatShare(v).fullString}
          </span>
        </div>
      ),
    },
    {
      field: 'percentRank',
      title: t('ABC Percent Rank', { ns: 'dataset' }),
      hasSort: true,
      hasFilter: true,
      hiddenInBrowser: true,
      filterOptions: generateFilterOptions('cumulativePercentRank'),
      render: (v: number) => formatter.formatShare(v).fullString,
    },
    {
      field: 'requiredQuantity',
      title: t(`Ordered (qty)`, { ns: 'dataset' }),
      hasSort: true,
      hasFilter: true,
      render: renderQtyValue,
    },
    {
      field: 'unpickableRequiredQuantity',
      title: t(`Unpickable (qty)`, { ns: 'dataset' }),
      hasSort: true,
      hasFilter: true,
      render: renderQtyValue,
    },
    ...(uomColumns as any),
    {
      field: 'name',
      title: t('Item Name', { ns: 'dataset' }),
      hasFilter: true,
      hasSort: true,
      filterOptions: generateFilterOptions('name'),
    },

    {
      field: 'description',
      title: t('Item Description', { ns: 'dataset' }),
      hasFilter: true,
      hasSort: true,
      filterOptions: generateFilterOptions('description'),
    },
    {
      field: 'skuGroup',
      title: t(`Item Group`, { ns: 'dataset' }),
      hasFilter: true,
      hasSort: true,
      filterOptions: generateFilterOptions('skuGroup'),
    },
    {
      field: 'subGroup',
      title: t(`Sub Group`, { ns: 'dataset' }),
      hasFilter: true,
      hasSort: true,
      filterOptions: generateFilterOptions('subGroup'),
    },
    {
      field: 'transportClass',
      title: t(`Transport Class`, { ns: 'dataset' }),
      hasFilter: true,
      hasSort: true,
      filterOptions: generateFilterOptions('transportClass'),
    },
    {
      field: 'stockCategory',
      title: t(`Stock Category`, { ns: 'dataset' }),
      hasFilter: true,
      hasSort: true,
      filterOptions: generateFilterOptions('stockCategory'),
    },
    {
      field: 'storageClass',
      title: t(`Storage Class`, { ns: 'dataset' }),
      hasFilter: true,
      hasSort: true,
      filterOptions: generateFilterOptions('storageClass'),
    },
    {
      field: 'pollutionClass',
      title: t(`Pollution Class`, { ns: 'dataset' }),
      hasFilter: true,
      hasSort: true,
      filterOptions: generateFilterOptions('pollutionClass'),
    },
    {
      field: 'maxStack',
      title: t(`Max Stackability`, { ns: 'dataset' }),
      hasFilter: true,
      hasSort: true,
      filterOptions: generateFilterOptions('maxStack'),
    },
    {
      field: 'orientationConstraint',
      title: t('Orientation Contraint', { ns: 'dataset' }),
      hasFilter: true,
      hasSort: true,
      filterOptions: generateFilterOptions('orientationConstraint'),
    },
  ];
}
